import { authClientWeb } from "react-redux-django-auth/web";

export type ApprovalStatus = 'APPROVED' | 'PENDING_APPROVAL' | 'REJECTED';
export type ChangeStatus = 'NONE' | 'PENDING_UPDATE' | 'PENDING_DELETE';

export interface CompanyPriceListItem {
  id: number;
  item_code: string;
  name: string;
  category: string;
  standard_price: string;
  cost_price: string;
  wholesale_price: string | null;
  min_price: string | null;
  description: string | null;
  delivery_timeline: string | null;
  is_active: boolean;

  // Governance & Moderation
  status: ApprovalStatus;
  status_display: string;
  change_status: ChangeStatus;
  change_status_display: string;
  pending_changes: Partial<CompanyPriceListItem> | null;

  created_by: number | null;
  created_by_name: string | null;
  proposed_by: number | null;
  proposed_by_name: string | null;
  proposed_at: string | null;
  approved_by: number | null;
  approved_by_name: string | null;
  approved_at: string | null;
  rejection_reason: string | null;
  profit_margin: string;

  created_at: string;
  updated_at: string;
}

export interface PriceListAnalytics {
  total_live_items: number;
  total_pending_queue: number;
  pending_additions: number;
  pending_updates: number;
  pending_deletions: number;
  categories: string[];
}

export interface PriceListResponse {
  success: boolean;
  data: CompanyPriceListItem[];
  analytics: PriceListAnalytics;
  count: number;
}

export interface FetchPriceListParams {
  search?: string;
  category?: string;
  status?: string;
  change_status?: string;
  review_queue?: boolean;
}

export interface PriceListPayload {
  name: string;
  category: string;
  standard_price: string | number;
  cost_price?: string | number;
  wholesale_price?: string | number | null;
  min_price?: string | number | null;
  description?: string | null;
  delivery_timeline?: string | null;
  is_active?: boolean;
}

const getClient = () => {
  return authClientWeb();
};

/**
 * Fetch catalog items with analytics and filter parameters
 */
export const fetchPriceList = async (params?: FetchPriceListParams, signal?: AbortSignal): Promise<PriceListResponse> => {
  const client = getClient();
  const queryParams = new URLSearchParams();

  if (params) {
    if (params.search && params.search.trim()) queryParams.append('search', params.search.trim());
    if (params.category && params.category !== 'ALL') queryParams.append('category', params.category);
    if (params.status && params.status !== 'ALL') queryParams.append('status', params.status);
    if (params.change_status && params.change_status !== 'ALL') queryParams.append('change_status', params.change_status);
    if (params.review_queue) queryParams.append('review_queue', 'true');
  }

  const queryStr = queryParams.toString();
  const url = queryStr ? `/price-list/?${queryStr}` : '/price-list/';
  const res = await client.get(url, { signal });
  return res.data;
};

/**
 * Retrieve single item by ID
 */
export const fetchPriceListItem = async (id: number): Promise<CompanyPriceListItem> => {
  const client = getClient();
  const res = await client.get(`/price-list/${id}/`);
  return res.data;
};

/**
 * Add a new item.
 * - SuperAdmin: published immediately as APPROVED.
 * - Admin: staged as PENDING_APPROVAL.
 */
export const createPriceListItem = async (payload: PriceListPayload): Promise<{ message: string; data: CompanyPriceListItem }> => {
  const client = getClient();
  const res = await client.post('/price-list/', payload);
  return res.data;
};

/**
 * Update an item.
 * - SuperAdmin: applied immediately to master record.
 * - Admin: staged in pending_changes as PENDING_UPDATE.
 */
export const updatePriceListItem = async (id: number, payload: Partial<PriceListPayload>): Promise<{ message: string; data: CompanyPriceListItem }> => {
  const client = getClient();
  const res = await client.patch(`/price-list/${id}/`, payload);
  return res.data;
};

/**
 * Delete an item.
 * - SuperAdmin: removed immediately.
 * - Admin: flagged as PENDING_DELETE.
 */
export const deletePriceListItem = async (id: number): Promise<{ message?: string; detail?: string }> => {
  const client = getClient();
  const res = await client.delete(`/price-list/${id}/`);
  return res.data || { detail: "Item deleted successfully." };
};

/**
 * SuperAdmin approval of pending addition, update, or deletion.
 */
export const approvePriceListItem = async (id: number): Promise<{ message: string; data?: CompanyPriceListItem }> => {
  const client = getClient();
  const res = await client.post(`/price-list/${id}/approve/`, { action: 'approve' });
  return res.data;
};

/**
 * SuperAdmin rejection of pending addition, update, or deletion.
 */
export const rejectPriceListItem = async (id: number, reason?: string): Promise<{ message: string; data?: CompanyPriceListItem }> => {
  const client = getClient();
  const res = await client.post(`/price-list/${id}/reject/`, { action: 'reject', reason });
  return res.data;
};

/**
 * Fetch distinct categories
 */
export const fetchPriceListCategories = async (): Promise<string[]> => {
  const client = getClient();
  const res = await client.get('/price-list/categories/');
  return res.data.categories || [];
};
